import uuid from 'node-uuid';
import { get, has, pickBy, isEqual } from 'lodash';
import moment from 'moment';
import { formatMandateToView, getIFTCreditorDebtorAmount } from '../../helpers/FormatPaymentHelper';
import {
	ARCHIVED_TAB,
	BACS_CREDIT_CODE,
	MANDATE_TYPE_BACS_CREDIT,
	MANDATE_TYPE_BACS_DEBIT,
	MANDATE_TYPE_CHAPS,
	MANDATE_TYPE_DIRECT_DEBIT,
	MANDATE_TYPE_IFT,
	MANDATE_TYPE_STANDING_ORDER,
	MANDATE_TYPE_SCHEDULE_PAYMENT,
	MANDATE_TYPE_SCHEDULE_TRANSFER,
	MANDATE_FILTER_OTHER_SCHEDULE,
	MANDATE_FILTER_ALL,
	MANDATE_FILTER_STANDING_ORDERS,
	MANDATE_FILTER_DIRECT_DEBITS,
	TYPE_CHAPS,
	TYPE_IFT,
} from '../../config';
import { paymentFrequencyOptions } from '../../config/paymentFrequencyOptions';
import {
	CONST_PAYEE,
	CONST_PAYEES,
	CONST_COLLECTION,
	CONST_COLLECTIONS,
	CONST_ON_DATE,
	CONST_ON_NUMBER,
	CONST_ON_CANCEL,
	CONST_FILE_UPLOAD,
} from '../../config/constants';
import { getAccountBySortCodeAccountNumber, getAccountById } from '../../states/accounts/selectors';
import { calcPaymentTypeCapable } from '../capabilities/selectors';
import { defaultColumnSort } from '../../managePayments/components/ManagePaymentListing/ColumnFormater';

const { BATCH_TYPE } = CONST_FILE_UPLOAD;

export const getAllStandingOrders = (state) => {
	const { managePayments: { standingOrders = [] } = {} } = state;
	return standingOrders.map((standingOrder) => {
		const isSinglePayment = has(standingOrder, 'single');
		const { account_id, sort_code, account_number } = standingOrder;
		const amountAndDateInfo = isSinglePayment
			? get(standingOrder, 'single')
			: get(standingOrder, 'schedule.next_payment');

		let mandateType = MANDATE_TYPE_STANDING_ORDER;
		if (isSinglePayment) {
			const isTransfer = !!(
				getAccountBySortCodeAccountNumber(state, sort_code, account_number) || getAccountById(state, account_id)
			);
			mandateType = isTransfer ? MANDATE_TYPE_SCHEDULE_TRANSFER : MANDATE_TYPE_SCHEDULE_PAYMENT;
		}
		return {
			...standingOrder,
			amount: get(amountAndDateInfo, 'amount.value'),
			when: get(amountAndDateInfo, 'when'),
			mandateType,
			from: get(standingOrder, 'fromAccount.displayName', ''),
			to: get(standingOrder, 'display_name', ''),
		};
	});
};
export const getAllDirectDebits = (state) => {
	const { managePayments: { directDebits = [] } = {} } = state;
	return directDebits.map((directDebit) => ({
		...directDebit,
		amount: get(directDebit, 'previous_payment.amount.value'),
		when: get(directDebit, 'previous_payment.when'),
		mandateType: MANDATE_TYPE_DIRECT_DEBIT,
		from: get(directDebit, 'fromAccount.displayName', ''),
		to: get(directDebit, 'display_name', ''),
	}));
};

export const getFromBacs = (mandate, isBacsCredit) => {
	const fromLabel = mandate.entries.length === 1 ? CONST_COLLECTION : CONST_COLLECTIONS;
	if (!isBacsCredit) {
		return {
			from: `[${mandate.entries.length} ${fromLabel}]`,
		};
	}
	return {
		from: get(mandate, 'fromAccount.displayName', ''),
	};
};

export const getToBacs = (mandate, isBacsCredit) => {
	const toLabel = mandate.entries.length === 1 ? CONST_PAYEE : CONST_PAYEES;
	if (isBacsCredit) {
		return {
			to: `[${mandate.entries.length} ${toLabel}]`,
		};
	}
	return {
		to: get(mandate, 'fromAccount.displayName', ''),
	};
};
export const getAllBacsMandates = (state) => {
	const { managePayments: { bacs = [] } = {} } = state;
	return bacs.map((mandate) => {
		const isBacsCredit = mandate.type === BACS_CREDIT_CODE;
		const mandateType = isBacsCredit ? MANDATE_TYPE_BACS_CREDIT : MANDATE_TYPE_BACS_DEBIT;
		const isFileUploadBatch = mandate.batch_type === BATCH_TYPE;
		return {
			...mandate,
			amount: get(mandate, 'total.value', ''),
			when: get(mandate, 'submission_date'),
			mandateType,
			bacsName: get(mandate, 'bacs_name'),
			...getFromBacs(mandate, isBacsCredit),
			...getToBacs(mandate, isBacsCredit),
			isFileUploadBatch,
		};
	});
};

export const getAllIftPayments = (state) => {
	const { managePayments: { ifts = [] } = {} } = state;
	return ifts.map((mandate) => {
		const fromAccountCurrency = get(mandate, 'from_account.currency', '');
		const { creditorAmount } = getIFTCreditorDebtorAmount(get(mandate, 'payment_amount', []), fromAccountCurrency);
		return {
			...mandate,
			amount: get(creditorAmount, 'value', ''),
			currency: get(creditorAmount, 'currency', ''),
			when: get(mandate, 'value_date'),
			mandateType: MANDATE_TYPE_IFT,
			from: get(mandate, 'fromAccount.displayName', ''),
			to: get(mandate, 'to_account.name', ''),
			type: TYPE_IFT,
		};
	});
};

export const getAllChapsPayments = (state) => {
	const { managePayments: { chaps = [] } = {} } = state;
	return chaps.map((mandate) => ({
		...mandate,
		amount: get(mandate.amount, 'value', ''),
		when: get(mandate, 'submission_date'),
		mandateType: MANDATE_TYPE_CHAPS,
		from: get(mandate, 'fromAccount.displayName', ''),
		to: get(mandate, 'to_account.name', ''),
		rawAmount: mandate.amount,
		isFileUploadBatch: isEqual(BATCH_TYPE, get(mandate, 'batch_type', false)),
	}));
};

export const getAllMandates = (state) => [
	...getAllStandingOrders(state),
	...getAllDirectDebits(state),
	...getAllBacsMandates(state),
	...getAllIftPayments(state),
	...getAllChapsPayments(state),
];

export const getEndConditionForMandateToEdit = (mandate) => {
	if (has(mandate, 'end_condition.when')) {
		return {
			stopPaymentOption: CONST_ON_DATE,
			stopPaymentOnDate: moment(mandate.end_condition.when),
		};
	}
	if (has(mandate, 'end_condition.number_of_payments')) {
		return {
			stopPaymentOption: CONST_ON_NUMBER,
			stopPaymentOnNumber: mandate.end_condition.number_of_payments,
		};
	}
	return {
		stopPaymentOption: CONST_ON_CANCEL,
	};
};

export const formatMandateToEdit = (mandate) => {
	const isSinglePayment = has(mandate, 'single');
	const amountAndDateInfo = isSinglePayment ? get(mandate, 'single') : get(mandate, 'schedule.next_payment');
	const { value: frequency } = paymentFrequencyOptions.find((frequencyOption) =>
		isEqual(frequencyOption.request, get(mandate, 'schedule.recurrence.frequency'))
	);
	return pickBy({
		...mandate,
		id: uuid.v1(),
		standing_order_uuid: mandate.id,
		type: isSinglePayment ? 'single' : 'repeating',
		amount: get(amountAndDateInfo, 'amount.value', '').toString(),
		date: moment(get(amountAndDateInfo, 'when')),
		fromAccount: {
			account_uuid: get(mandate, 'fromAccount.accountId'),
		},
		frequency,
		...getEndConditionForMandateToEdit(mandate),
		toAccount: {
			account_uuid: get(mandate, 'account_id'),
			sort_code: get(mandate, 'sort_code'),
			account_number: get(mandate, 'account_number'),
			display_name: get(mandate, 'display_name'),
		},
		isInternalPayment: get(mandate, 'is_internal_payment', undefined),
	});
};

export const getAllActiveMandates = (state) => getAllMandates(state).filter((mandate) => mandate.is_active);

export const getAllArchivedMandates = (state) => getAllMandates(state).filter((mandate) => !mandate.is_active);

export const getMandateActiveTab = (state) => state.managePayments.mandateActiveTab;

export const getMandateActiveFilter = (state) => state.managePayments.mandateActiveFilter;

export const hasIFTPayment = (state) => {
	if (getMandateActiveTab(state) === ARCHIVED_TAB) {
		return !!get(state, 'managePayments.ifts', []).filter((payment) => !payment.is_active).length;
	}
	return (
		!!get(state, 'managePayments.ifts', []).filter((payment) => payment.is_active).length &&
		[MANDATE_FILTER_OTHER_SCHEDULE, MANDATE_FILTER_ALL].includes(getMandateActiveFilter(state))
	);
};

export const getMandateToView = (state) => formatMandateToView(state.managePayments.mandateToView);

export const getMandateToEdit = ({ managePayments: { mandateToEdit, standingOrders, ifts, chaps } }) => {
	const mandate = [
		...standingOrders,
		...ifts.map((mandate) => ({ ...mandate, mandateType: TYPE_IFT })),
		...chaps.map((mandate) => ({ ...mandate, mandateType: TYPE_CHAPS })),
	].find((mandate) => mandate.id === mandateToEdit);
	switch (mandate.mandateType) {
		case TYPE_CHAPS:
		case TYPE_IFT:
			return mandate;
		default:
			return formatMandateToEdit(mandate);
	}
};

export const getIsMandateEditable = (state) => {
	const { managePayments: { mandateToView: { mandateType = '', actions_available } } = {} } = state;
	const isPaymentTypeCapable = calcPaymentTypeCapable(state, mandateType);

	return (
		isPaymentTypeCapable &&
		(([MANDATE_TYPE_SCHEDULE_PAYMENT, MANDATE_TYPE_STANDING_ORDER].includes(mandateType) &&
			actions_available['/account/mandates/so/write']) ||
			[MANDATE_TYPE_IFT, MANDATE_TYPE_CHAPS].includes(mandateType))
	);
};

export const getActiveSortOrder = (state) => get(state, 'managePayments.activeSortOrder', defaultColumnSort);
export const getArchiveSortOrder = (state) => get(state, 'managePayments.archiveSortOrder', defaultColumnSort);

export const getActivePage = (state) => get(state, 'managePayments.mandateActivePage', 1);
export const getArchivePage = (state) => get(state, 'managePayments.mandateArchivePage', 1);

export const getMandateTotals = (state) => ({
	mandateActivePageTotal: get(state, 'managePayments.mandateActivePageTotal', null),
	mandateArchivePageTotal: get(state, 'managePayments.mandateArchivePageTotal', null),
	mandateActiveTotal: get(state, 'managePayments.mandateActiveTotal', 0),
	mandateArchiveTotal: get(state, 'managePayments.mandateArchiveTotal', 0),
});

export const getFilteredResults = (activeMandates, selectedFilter) => {
	const mandateFilterAndTypeMapping = {
		[MANDATE_FILTER_STANDING_ORDERS]: [MANDATE_TYPE_STANDING_ORDER],
		[MANDATE_FILTER_DIRECT_DEBITS]: [MANDATE_TYPE_DIRECT_DEBIT],
		[MANDATE_FILTER_OTHER_SCHEDULE]: [
			MANDATE_TYPE_BACS_CREDIT,
			MANDATE_TYPE_BACS_DEBIT,
			MANDATE_TYPE_SCHEDULE_PAYMENT,
			MANDATE_TYPE_SCHEDULE_TRANSFER,
			MANDATE_TYPE_IFT,
			MANDATE_TYPE_CHAPS,
		],
	};
	if (selectedFilter === MANDATE_FILTER_ALL) {
		return activeMandates;
	}
	return activeMandates.filter(({ mandateType }) => mandateFilterAndTypeMapping[selectedFilter].includes(mandateType));
};
